import React, { useState } from "react";
import { Box, Card, CardContent, Grid, Typography, Stack } from "@mui/material";
import { CustomDialog } from "@web-applications/allinga-web-components";
import { t } from "i18next";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useAtom } from "jotai";
import { notificationAtom } from "atoms/notification";

interface Example {
  title: string;
  description: string;
  ssml: string;
}

interface Props {
  open?: boolean;
  handleOnClose?: () => void;
  onInsertExample?: (ssml: string) => void;
  insertedExamples?: Set<string>;
}

export default function CustomDialogExampleScenarios({
  open = true,
  handleOnClose,
  onInsertExample,
  insertedExamples = new Set(),
}: Props) {
  const [selectedExample, setSelectedExample] = useState<Example | null>(null);
  const [, setNotification] = useAtom(notificationAtom);

  const examples: Example[] = [
    {
      title: t("tts.helpAndLearning.exampleOneTitle"),
      description: t("tts.helpAndLearning.exampleOneDescription"),
      ssml: `<style name='happy' intensity='x-strong'>Achtung! Passagiere des Fluges <say-as interpret-as='spell-out'>BA215</say-as> nach Berlin begeben sich bitte umgehend zum Gate <say-as interpret-as='cardinal'>7</say-as> zum Boarding. <lang language='en-US'>Attention, please! Passengers on flight <say-as interpret-as='spell-out'>BA215</say-as> to Berlin, kindly proceed to gate <say-as interpret-as='cardinal'>7</say-as> immediately</lang></style>`,
    },
    {
      title: t("tts.helpAndLearning.exampleTwoTitle"),
      description: t("tts.helpAndLearning.exampleTwoDescription"),
      ssml: `<style name='happy' intensity='x-strong'><voice name='emilie' language='de-DE'>Die tägliche Dosis Optimismus! Wir sammeln täglich die besten Good News und wichtigsten lösungsorientierten Nachrichten. Kostenlos, übersichtlich und topaktuell. Heute starten wir mit 6 guten Nachrichten. Los geht’s mit Nummer 1: Italien ist auch dieses Jahr das beliebteste Urlaubsland.</voice></style>`,
    },
    {
      title: t("tts.helpAndLearning.exampleThreeTitle"),
      description: t("tts.helpAndLearning.exampleThreeDescription"),
      ssml: `<voice name='benjamin'><style name='positive' intensity='x-strong'>Achtung: Unwetterwarnung. Der Deutsche Wetterdienst warnt vor starkem Regen und Gewittern in Bayern und Baden-Württemberg. Bitte sichern Sie Keller und Tiefgaragen und vermeiden Sie unnötige Fahrten.</style></voice>`,
    },
  ];

  const handleCardClick = (example: Example) => {
    setSelectedExample(example);
    if (insertedExamples.has(example.ssml)) {
      setNotification({
        isOpen: true,
        severity: "warning",
        message: t("tts.helpAndLearning.duplicateExample"),
      });
    }
  };

  const handleApply = () => {
    if (selectedExample) {
      onInsertExample?.(selectedExample.ssml);
    }
    handleOnClose?.();
  };

  const handleClose = () => {
    setSelectedExample(null);
    handleOnClose?.();
  };

  const getPreviewSSML = (ssml: string) =>
    ssml.replace(/^<speak>|<\/speak>$/g, "").trim();

  const footerText = selectedExample ? (
    <Box sx={{ color: "text.secondary" }}>
      <Typography variant="overline" fontFamily="monospace">
        {getPreviewSSML(selectedExample.ssml)}
      </Typography>
    </Box>
  ) : undefined;

  return (
    <CustomDialog
      id="example-scenarios-dialog"
      open={open}
      header={t("tts.helpAndLearning.button4")}
      handleOnClose={handleClose}
      showCloseIcon={true}
      hideActions={false}
      footerText={footerText}
      maxWidth="md"
      height="auto"
      primaryButtonText={t("buttons.apply")}
      onPrimaryButtonClick={handleApply}
      isPrimaryButtonDisabled={
        !selectedExample || insertedExamples.has(selectedExample.ssml)
      }
      tertiaryButtonText={t("buttons.cancel")}
      onTertiaryButtonClick={handleClose}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" mb={3}>
          {t("tts.helpAndLearning.exampleDialogText")}
        </Typography>

        <Grid container spacing={3}>
          {examples.map((example) => {
            const isSelected = selectedExample?.title === example.title;
            return (
              <Grid item xs={12} md={6} lg={4} key={example.title}>
                <Card
                  onClick={() => handleCardClick(example)}
                  sx={{
                    height: "100%",
                    cursor: "pointer",
                    border: isSelected ? "2px solid" : "2px solid transparent",
                    borderColor: isSelected ? "primary.main" : "transparent",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="h6">{example.title}</Typography>
                        {isSelected && (
                          <CheckCircleOutlineIcon
                            color="primary"
                            fontSize="small"
                          />
                        )}
                      </Stack>

                      <Typography variant="body2" color="text.secondary">
                        {example.description}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </CustomDialog>
  );
}
